/*
  This module exports a factory function, create, to generate TransformComposer
  objects. A TransformComposer object provides an interface to apply various
  transformations to JSON attributes and to generate the resulting Nexla transform
  specification.
*/
var _ = require('lodash'),
  Base64 = require('js-base64').Base64;

var _TSOps = {
  'array.sort': 'sort',
  'currency.convert': 'USDConversion',
  'currency.convertString': 'USDConversionString',
  geo: 'GeoDetection',
  lookup: 'toMapValue',
  lookup2: 'toMapValue2',
  lookupList: 'getMap',
  toString: 'toString',
  userAgent: 'UserAgentDetection',
  vin: 'VINDetection',
  concatSingle: 'concat',
  concatMultiple: 'concat',
  joinMultiple: 'join',
  getNexsetData: 'getNexsetData',
};

var _ArrayPathPattern = /\.\[(\*|\d?)\]\./;

// REMOVE when Transform Service supports a spec endpoint for it
var _TMetadataSpec = {
  NX_sourceType: {
    attributeName: 'sourceType',
    description: 'Data source type',
    type: 'string',
    example: 'S3',
  },
  NX_sourceBucket: {
    attributeName: 'bucket',
    description: 'Data source top-level bucket or directory',
    type: 'integer',
    example: 'daily-logs.example.com',
  },
  NX_sourcePath: {
    attributeName: 'sourceKey',
    description:
      'Path to ingested resource from top-level bucket, directory, api path, database, etc.',
    type: 'string',
    example: 'hourly_events/2017-07-28/1700.json',
  },
  NX_sourceOffset: {
    attributeName: 'sourceOffset',
    description: 'Data source offset to record',
    type: 'integer',
    example: '100',
  },
  NX_runId: {
    attributeName: 'runId',
    description: 'Unique flow execution run id for the source',
    type: 'integer',
    example: 1638205638431,
  },
  NX_ingestTime: {
    attributeName: 'ingestTime',
    description: 'Nexla record ingestion time in Unix Epoch milliseconds',
    type: 'integer',
    example: 1498776641620,
  },
  NX_transformTime: {
    attributeName: 'transformTime',
    description: 'Nexla record transformation time in Unix Epoch milliseconds',
    type: 'integer',
    example: 1498776641620,
  },
  NX_transformTimeISO8601: {
    attributeName: 'transformTimeISO8601',
    description: 'Nexla record transformation time in ISO 8601 format',
    type: 'string',
    example: '2017-06-29T22:50:41Z',
  },
  NX_trackerId: {
    attributeName: 'trackerId',
    description:
      'Unique record tracker that contains lineage information about this record as it flows through Nexla.',
    type: 'string',
    example:
      'cjExMDA5OjIyNjY2OnRyYWRlc18wOV8yNy5jc3Y6MjoxOjE\n1NjcwNTgyNDY5MDA7MjI2NzE6MzU5OTc7ODgzNzoxOjI5Mw',
  },
  NX_tags: {
    attributeName: 'tags',
    description: 'Optional key-value properties captured for this source',
    type: 'object',
    example: '{"from_email": "test@nexla.com"}',
  },
  NX_resourceType: {
    attributeName: 'resourceType',
    description: 'Resource type for the Resource Id metadata',
    type: 'string',
    example: 'SOURCE',
  },
  NX_resourceId: {
    attributeName: 'resourceId',
    description: 'Resource ID of the resource at the root of this flow',
    type: 'integer',
    example: 12302,
  },
};

module.exports = {
  create: function (state) {
    return new TransformComposer(state);
  },
};

function ArrayPaths(srcAttr, dstAttr) {
  this._src = null;
  this._dst = null;
  if (_.isObject(srcAttr)) {
    this._resetFromState(srcAttr);
  } else {
    this.setAttrs(srcAttr, dstAttr);
  }
}

ArrayPaths.prototype._resetFromState = function (state) {
  this._srcAttr = state._srcAttr;
  this._dstAttr = state._dstAttr;
  if (_.isObject(state._src)) {
    this._src = _.cloneDeep(state._src);
  }
  if (_.isObject(state._dst)) {
    this._dst = _.cloneDeep(state._dst);
  }
};

ArrayPaths.prototype.isValid = function () {
  return this.hasSrcArrayPath() && this.hasDstArrayPath();
};

ArrayPaths.prototype.hasSrcArrayPath = function () {
  return !!this._src;
};

ArrayPaths.prototype.hasDstArrayPath = function () {
  return !!this._dst;
};

ArrayPaths.prototype.getSrcAttr = function () {
  return this._srcAttr;
};

ArrayPaths.prototype.setAttrs = function (srcAttr, dstAttr) {
  if (!srcAttr) {
    // Called without paths, just return.
    return;
  }

  var arrayPath = this.parseArrayAttr(srcAttr);
  if (!arrayPath) {
    // That path doesn't contain array syntax.
    // No state change.
    return;
  }

  this._srcAttr = srcAttr;
  this._dstAttr = dstAttr === undefined ? this._srcAttr : dstAttr;

  this._src = arrayPath;
  this._dst =
    this._dstAttr === this._srcAttr ? _.cloneDeep(this._src) : this.parseArrayAttr(dstAttr);
};

ArrayPaths.prototype.parseArrayAttr = function (attr) {
  var desc = null;
  var m = _ArrayPathPattern.exec(attr);
  if (m === null || m.length < 2) {
    return;
  }
  desc = {
    attr: attr,
    index: m[1] === '' ? '*' : m[1],
    prefix: m['input'].substring(0, m['index']),
    postfix: m['input'].substring(m['index'] + m[0].length),
  };
  desc.postfixDepth = desc.postfix.split('.').length;
  desc.shiftDst = `${desc.prefix}.[&${desc.postfixDepth}].${desc.postfix}`;
  desc.traverse = `${desc.prefix}.${desc.index}.${desc.postfix}`;
  return desc;
};

ArrayPaths.prototype.getSrcPostfix = function () {
  return !this._src ? null : this._src.postfix;
};

ArrayPaths.prototype.getSrcTraverse = function () {
  return !this._src ? null : this._src.traverse;
};

ArrayPaths.prototype.getDstTraverse = function () {
  if (!this.isValid()) {
    return null;
  }

  var dstTraverse = !this.requiresPostShift()
    ? this._dst.traverse
    : this._src.prefix + '.' + this._src.index + '.' + this._dst.postfix;

  return dstTraverse;
};

ArrayPaths.prototype.getDstPostfixDepth = function () {
  return !this._dst ? null : this._dst.postfixDepth;
};

ArrayPaths.prototype.getSrcShift = function () {
  return !this._src ? null : this._src.shiftDst;
};

ArrayPaths.prototype.getShiftDst = function () {
  return !!this._dst ? this._dst.shiftDst : null;
};

ArrayPaths.prototype.isOverlapping = function () {
  return this._src.postfix === this._dst.postfix;
};

ArrayPaths.prototype.requiresPostShift = function () {
  return this._src.prefix !== this._dst.prefix;
};

ArrayPaths.prototype.getPostShiftSpec = function () {
  if (!this.isValid()) {
    return null;
  }

  // If we're modifying in-place, just return null. Otherwise,
  // return a Jolt shift spec.
  if (this._src.prefix === this._dst.prefix) {
    return null;
  }

  var traverse = `${this._src.prefix}.${this._dst.index}.${this._dst.postfix}`;
  var spec = {};

  // This shift rule preserves everything moved by the
  // initial shift block, including attributes added with
  // TransformComposer.includeAttribute()...
  // MOVE to TransformBuilder--only required once at top of block
  _.set(spec, '*', '&0');

  // Then this rule traverses down to the attribute in the
  // original object array and moves it into the destination
  // object array.
  _.set(spec, traverse, this._dst.shiftDst);

  return spec;
};

ArrayPaths.prototype.matchesDstAttr = function (attr) {
  if (!this._dst) {
    return false;
  }
  // The input attr may be an exact match, or it could
  // be the intermediate modifer path, which begins with
  // the path to the source array.
  // eslint-disable-next-line
  return this._dst.attr == attr || this.getDstTraverse() == attr;
};

function Modifier(composer, state) {
  this._TC = composer;
  this._srcAttrs = [];
  this._dstAttr = null;
  this._spec = null;
  this._arrayPaths = new ArrayPaths();
  this._dataMaps = [];
  this._dataSets = [];
  this._attributeFuncName = this._TC.hasTransformFeature('nexla_attribute_syntax') ? '%' : '@';

  if (_.isObject(state)) {
    this._resetFromState(state);
  }
}

Modifier.prototype._resetFromState = function (state) {
  this._srcAttrs = state._srcAttrs;
  this._dstAttr = state._dstAttr;
  this._spec = state._spec;
  this._dataMaps = state._dataMaps;
  this._dataSets = state._dataSets;
  this._secondPass = state._secondPass;

  if (_.isObject(state._arrayPaths)) {
    this._arrayPaths = new ArrayPaths(state._arrayPaths);
  }

  // We are restored to state as if Modifier.evaluate()
  // has already been called, so this._arguments should
  // be empty. But we need to populate _immutableArguments,
  // in case state is saved again.
  this._arguments = [];
  this._immutableArguments = [];
  var self = this;

  _.forEach(state._arguments, function (arg) {
    if (typeof arg === 'string') {
      self._immutableArguments.push(arg);
    } else if (_.isObject(arg)) {
      self._immutableArguments.push(new Modifier(self._TC, arg));
    }
  });

  return this;
};

Modifier.prototype.getStateObject = function () {
  var state = {
    _func: this._immutableArguments[0],
    _srcAttrs: this._srcAttrs,
    _dstAttr: this._dstAttr,
    _spec: this._spec,
    _arrayPaths: this.hasArrayPaths() ? this._arrayPaths : null,
    _dataMaps: this._dataMaps,
    _dataSets: this._dataSets,
    _attributeFuncName: this._attributeFuncName,
    _arguments: [],
    _secondPass: this._secondPass,
  };

  _.forEach(this._immutableArguments, function (arg) {
    if (typeof arg === 'string') {
      state._arguments.push(arg);
    } else if (_.isObject(arg)) {
      state._arguments.push(arg.getStateObject());
    }
  });

  return state;
};

Modifier.prototype.setArguments = function (args) {
  this._immutableArguments = Array.from(args);
  this._arguments = Array.from(args);
  return this;
};

Modifier.prototype.done = function (dstAttr, secondPass) {
  this._evaluate();
  this._dstAttr = !!dstAttr ? dstAttr : this._srcAttrs[0];
  if (!!secondPass) {
    this._secondPass = true;
  }
  if (!!this._dstAttr) {
    this._arrayPaths.setAttrs(this._arrayPaths.getSrcAttr(), this._dstAttr);
    this._TC._pushModifier(this);
  }
  return this;
};

Modifier.prototype.getSpec = function () {
  return this._spec;
};

Modifier.prototype.getDstAttr = function () {
  return this.hasArrayPaths() ? this._arrayPaths.getDstTraverse() : this._dstAttr;
};

Modifier.prototype.getSrcAttrs = function () {
  return this._srcAttrs;
};

Modifier.prototype.getDataMaps = function () {
  return this._dataMaps;
};

Modifier.prototype.getDataSets = function () {
  return this._dataSets;
};

Modifier.prototype.hasArrayPaths = function () {
  return !!this._arrayPaths && this._arrayPaths.isValid();
};

Modifier.prototype.getArrayPaths = function () {
  return this.hasArrayPaths() ? this._arrayPaths : null;
};

Modifier.prototype._getStringLiteral = function (str) {
  return _.startsWith(str, '$.') ? str.substring(2) : null;
};

Modifier.prototype._getNumberLiteral = function (str) {
  if (!_.startsWith(str, '$_')) {
    return null;
  }

  let toConvert = str.substring(2);
  return _.isFinite(_.toNumber(toConvert)) ? _.toFinite(toConvert) : null;
};

Modifier.prototype._updateSrcAttrs = function () {
  var specs = Array.from(arguments);
  var srcAttrLists = [];
  var arrayPaths = null;
  _.forEach(specs, function (spec) {
    if (Array.isArray(spec._srcAttrs)) {
      srcAttrLists.push(spec._srcAttrs);
    }
    if (!!spec._arrayPaths) {
      arrayPaths = spec._arrayPaths;
    }
  });
  srcAttrLists.unshift(this._srcAttrs);
  this._srcAttrs = _.union(...srcAttrLists);
  if (!!arrayPaths) {
    this._arrayPaths.setAttrs(arrayPaths.getSrcAttr());
  }
};

Modifier.prototype._evaluate = function (src) {
  if (typeof src == 'number') {
    return { _spec: `${src}`, _srcAttrs: [] };
  }

  if (typeof src === 'string') {
    let metaAttr = _TMetadataSpec[src];
    if (metaAttr !== undefined) {
      return { _spec: `^${metaAttr.attributeName}`, _srcAttrs: [src] };
    }

    let literal = this._getStringLiteral(src);
    if (literal !== null) {
      return { _spec: `'${literal}'`, _srcAttrs: [] };
    }

    let numberLiteral = this._getNumberLiteral(src);
    if (numberLiteral !== null) {
      return { _spec: `${numberLiteral}`, _srcAttrs: [] };
    }

    // Check for array source attribute and adjust
    // reference accordingly...but ONLY if we don't
    // already have a source array path. (LIMITATION/IMPROVE)
    if (!this._arrayPaths.hasSrcArrayPath()) {
      // Note, this will not change the state of _arrayPaths
      // if src does not actually contain array path syntax.
      this._arrayPaths.setAttrs(src);
    }

    var spec = {
      _srcAttrs: [src],
      _arrayPaths: this._arrayPaths,
    };

    if (spec._arrayPaths === null || !spec._arrayPaths.isValid()) {
      spec._spec = `${this._attributeFuncName}(<<D>>,'${src}')`;
    } else {
      spec._spec = `${this._attributeFuncName}(<<D>>,'${spec._arrayPaths.getSrcPostfix()}')`;
    }

    return spec;
  }

  if (Array.isArray(src)) {
    let self = this;
    let specList = [];
    let srcAttrs = [];
    let arrayPaths = null;
    _.forEach(src, function (s) {
      let spec = self._evaluate(s);
      specList.push(spec._spec);
      srcAttrs = _.union(srcAttrs, spec._srcAttrs);
      if (!!spec._arrayPaths) {
        arrayPaths = spec._arrayPaths;
      }
    });
    return {
      _spec: `[${specList}]`,
      _srcAttrs: srcAttrs,
      _arrayPath: arrayPaths,
    };
  }

  if (_.isObject(src)) {
    src._evaluate();
    this._updateSrcAttrs(src);
    return src;
  }

  if (src !== undefined || this._arguments.length < 1) {
    return null;
  }

  let op = this._arguments.shift();
  let tsOp = _TSOps[op] || op;
  let intArg = null;
  let strArg = null;

  switch (op) {
    case 'abs':
    case 'array.sort':
    case 'avg':
    case 'concatSingle':
    case 'concatMultiple':
    case 'firstElement':
    case 'iso8601FromEpoch':
    case 'iso8601FromString':
    case 'lastElement':
    case 'max':
    case 'min':
    case 'noop':
    case 'size':
    case 'strlen':
    case 'toDouble':
    case 'toInteger':
    case 'toLong':
    case 'toLower':
    case 'toString':
    case 'toUpper':
    case 'trim':
    case 'toJsonString':
    case 'not?':
    case 'empty?':
      src = this._evaluate(this._arguments.shift());
      this._updateSrcAttrs(src);
      this._spec = `=${tsOp}(${src._spec})`;
      break;

    case 'substring':
      {
        intArg = this._arguments.shift();
        let intArg2 = this._arguments.shift();
        src = this._evaluate(this._arguments.shift());
        this._updateSrcAttrs(src);
        this._spec = `=${tsOp}(${intArg},${intArg2},${src._spec})`;
      }
      break;

    case 'bucket':
    case 'elementAt':
    case 'precision':
      intArg = this._arguments.shift();
      src = this._evaluate(this._arguments.shift());
      this._updateSrcAttrs(src);
      this._spec = `=${tsOp}(${intArg},${src._spec})`;
      break;

    case 'getNexsetData':
      let dataSetId = this._arguments.shift();
      this._dataSets.push(dataSetId);
      let apiKey = this._arguments.shift();
      let noCache = this._arguments.shift();
      let selectors = this._arguments.shift();
      let selectorsArray = [];
      for (var k in selectors) {
        // Here we convert the getNexsetData indexing
        // object passed by the caller into the
        // array format the Transform Service
        // expects. We treat the keys as string
        // literals and the values as input attributes
        // (unless the caller explicitly sets the
        // value as a string or numeric literal).
        selectorsArray.push('$.' + k);
        selectorsArray.push(selectors[k]);
      }
      src = this._evaluate(selectorsArray);
      this._updateSrcAttrs(src);
      this._spec = `=${tsOp}(${noCache},${dataSetId},'${apiKey}',${src._spec})`;
      break;

    case 'lookup':
      {
        intArg = this._arguments.shift();
        this._dataMaps.push(intArg);
        src = this._evaluate(this._arguments.shift());
        this._updateSrcAttrs(src);
        let subKey = this._arguments.shift();
        if (typeof subKey === 'string') {
          tsOp = _TSOps['lookup2'] || tsOp;
          this._spec = `=${tsOp}(${intArg},${src._spec},'${subKey}')`;
        } else {
          this._spec = `=${tsOp}(${intArg},${src._spec})`;
        }
      }
      break;

    case 'lookupList':
    case 'oneHotEncoding': {
      intArg = this._arguments.shift();
      this._dataMaps.push(intArg);
      src = this._evaluate(this._arguments.shift());
      this._updateSrcAttrs(src);
      this._spec = `=${tsOp}(${intArg},${src._spec})`;
      break;
    }

    case 'extractDate':
    case 'grok':
    case 'geo':
    case 'integerHash':
    case 'split':
    case 'stringHash':
    case 'userAgent':
    case 'vin':
      strArg = this._arguments.shift();
      src = this._evaluate(this._arguments.shift());
      this._updateSrcAttrs(src);
      this._spec = `=${tsOp}('${strArg}',${src._spec})`;
      break;

    // 'join' is intentionally separated from the strArg+src group above so it
    // sits next to 'joinMultiple' below — both emit the same backend op
    // (=join(...)) and reading them together makes the single- vs multi-source
    // variants easier to follow.
    case 'join':
      strArg = this._arguments.shift();
      src = this._evaluate(this._arguments.shift());
      this._updateSrcAttrs(src);
      this._spec = `=${tsOp}('${strArg}',${src._spec})`;
      break;

    case 'joinMultiple': {
      strArg = this._arguments.shift();
      let joinSrcs = Array.from(this._arguments);
      this._arguments = [];
      src = this._evaluate(joinSrcs);
      this._updateSrcAttrs(src);
      this._spec = `=${tsOp}('${strArg}',${src._spec})`;
      break;
    }

    case 'regexFind':
    case 'regexMatch':
      strArg = Base64.encode(this._arguments.shift());
      src = this._evaluate(this._arguments.shift());
      this._srcAttrs = _.union(this._srcAttrs, src._srcAttrs);
      this._spec = `=${tsOp}('${strArg}',${src._spec})`;
      break;

    case 'filter':
      strArg = Base64.encode(this._arguments.shift());
      src = this._evaluate(this._arguments.shift());
      this._srcAttrs = _.union(this._srcAttrs, src._srcAttrs);
      this._spec = `=${tsOp}('base64','${strArg}',${src._spec})`;
      break;

    case 'custom':
      intArg = this._arguments.shift();
      src = this._evaluate(this._arguments.shift());
      this._updateSrcAttrs(src);
      this._spec = `=${tsOp}(${intArg},${src._spec})`;
      break;

    case 'convertTimeZone':
      {
        let strOrNullArg1 = this._arguments.shift();
        strArg = this._arguments.shift();
        src = this._evaluate(this._arguments.shift());
        this._updateSrcAttrs(src);
        this._spec = strOrNullArg1
          ? `=${tsOp}('${strOrNullArg1}','${strArg}',${src._spec})`
          : `=${tsOp}(${strOrNullArg1},'${strArg}',${src._spec})`;
      }
      break;

    case 'replaceAll':
      {
        let strArg1 = this._arguments.shift();
        let strArg2 = this._arguments.shift();
        src = this._evaluate(this._arguments.shift());
        this._updateSrcAttrs(src);
        this._spec = `=${tsOp}('${strArg1}','${strArg2}',${src._spec})`;
      }
      break;

    case 'currency.convert':
    case 'currency.convertString':
      intArg = this._arguments.shift();
      strArg = this._arguments.shift();
      src = this._evaluate(this._arguments.shift());
      this._dataMaps.push(intArg);
      this._updateSrcAttrs(src);
      this._spec = `=${tsOp}(${intArg},'${strArg}',${src._spec})`;
      break;

    case 'timestampDifference':
      {
        let diffUnit = this._arguments.shift();
        let fromTs = this._evaluate(this._arguments.shift());
        let tsToSubtract = this._evaluate(this._arguments.shift());
        this._updateSrcAttrs(fromTs, tsToSubtract);
        this._spec = `=${tsOp}('${diffUnit}',${tsToSubtract._spec},${fromTs._spec})`;
      }
      break;

    case 'add':
    case 'diff':
    case 'multiply':
    case 'div':
    case 'equal?':
    case 'lessThan?':
    case 'greaterThan?':
    case 'and?':
    case 'or?':
      {
        let src1 = this._evaluate(this._arguments.shift());
        let src2 = this._evaluate(this._arguments.shift());
        this._updateSrcAttrs(src1, src2);
        this._spec = `=${tsOp}(${src1._spec},${src2._spec})`;
      }
      break;

    case 'ifElse':
      {
        let criteria = this._evaluate(this._arguments.shift());
        let truthyAttr = this._evaluate(this._arguments.shift());
        let falsyAttr = this._evaluate(this._arguments.shift());
        this._updateSrcAttrs(criteria, truthyAttr, falsyAttr);
        this._spec = `=${tsOp}(${criteria._spec},${truthyAttr._spec},${falsyAttr._spec})`;
      }
      break;

    default:
      break;
  }

  return this;
};

function TransformComposer(state) {
  this.array = new TxArray(this);
  this.currency = new TxCurrency(this);
  this.date = new TxDate(this);
  this.extract = new TxExtract(this);
  this.map = new TxMap(this);
  this.math = new TxMath(this);
  this.string = new TxString(this);
  this.conditional = new TxConditional(this);
  this.ai = new TxAi(this);

  this._tx_features = {
    nexla_attribute_syntax: true,
    base64_filters: true,
    include_data_set_deps: false,
  };

  this.reset(state);
}

TransformComposer.prototype.getMetadataSpec = function () {
  return _TMetadataSpec;
};

TransformComposer.prototype.reset = function (state) {
  this.resetTransforms();
  this.resetFilters();
  if (_.isObject(state)) {
    this._resetFromState(state);
  }
};

TransformComposer.prototype.resetTransforms = function () {
  this._tx_stack = [];
  this._tx_redo_stack = [];
};

TransformComposer.prototype.resetFilters = function () {
  this._ft_stack = [];
  this._ft_redo_stack = [];
};

TransformComposer.prototype._resetFromState = function (state) {
  var self = this;
  this._tx_custom = state._tx_custom;
  this._ft_stack = state._ft_stack;
  this._tx_features = state._tx_features;
  _.forEach(state._tx_stack, function (op) {
    switch (op.op) {
      case 'modify':
        self._tx_stack.push({
          op: op.op,
          modifier: self._createModifierFromState(op.modifier),
        });
        break;

      default:
        self._tx_stack.push(_.cloneDeep(op));
        break;
    }
  });
};

TransformComposer.prototype._createModifier = function () {
  return new Modifier(this).setArguments(arguments);
};

TransformComposer.prototype._createModifierFromState = function (state) {
  return new Modifier(this, state);
};

TransformComposer.prototype._pushModifier = function (modifier) {
  this._tx_stack.push({ op: 'modify', modifier: modifier });
};

/* Public Methods */

TransformComposer.prototype.setTransformFeatures = function (features) {
  if (_.isObject(features)) {
    this._tx_features = _.cloneDeep(features);
  }
};

TransformComposer.prototype.hasTransformFeature = function (feature) {
  var has = false;
  if (_.isObject(this._tx_features)) {
    has = !!this._tx_features[feature];
  }
  return has;
};

TransformComposer.prototype.getTransformObject = function () {
  var builder = new TransformBuilder(
    this._tx_features,
    this._tx_stack,
    this._ft_stack,
    this._tx_custom,
  );
  return builder.build();
};

TransformComposer.prototype.getTransformJSON = function () {
  return JSON.stringify(this.getTransformObject());
};

TransformComposer.prototype.getTransformJoltSpecs = function () {
  return JSON.stringify(this.getTransformObject().transforms);
};

TransformComposer.prototype.getTransformJoltSpecsArray = function () {
  return this.getTransformObject().transforms;
};

TransformComposer.prototype.getStateObject = function () {
  var builder = new StateBuilder(
    this._tx_stack,
    this._ft_stack,
    this._tx_custom,
    this._tx_features,
  );
  return builder.build();
};

TransformComposer.prototype.getStateJSON = function () {
  return JSON.stringify(this.getStateObject());
};

TransformComposer.prototype.setFlattenFirst = function (flattenFirst) {
  this._tx_stack.push({ op: 'setFlattenFirst', flattenFirst: flattenFirst });
};

TransformComposer.prototype.getFlattenFirst = function () {
  var flattenFirst = false;
  // Here we run a mini-version of _applyStack
  // to determinte the net effect of the all the
  // "setFlattenFirst" actions.
  _.forEach(this._tx_stack, function (op) {
    if (op.op === 'setFlattenFirst') {
      var ff = !!op.flattenFirst;
      if (flattenFirst !== ff) {
        flattenFirst = ff;
      }
    }
  });
  return flattenFirst;
};

TransformComposer.prototype.setNestedFirst = function (nestedFirst) {
  this._tx_stack.push({ op: 'setNestedFirst', nestedFirst: nestedFirst });
};

TransformComposer.prototype.setIncludeAll = function (includeAll) {
  this._tx_stack.push({ op: 'setIncludeAll', includeAll: includeAll });
};

TransformComposer.prototype.getIncludeAll = function () {
  var includeAll = true;
  // Here we run a mini-version of _applyStack
  // to determinte the net effect of the all the
  // "setIncludeAll" actions.
  _.forEach(this._tx_stack, function (op) {
    if (op.op === 'setIncludeAll') {
      var ia = !!op.includeAll;
      if (includeAll !== ia) {
        includeAll = ia;
      }
    }
  });
  return includeAll;
};

TransformComposer.prototype.includeMetadataAttribute = function (srcAttr, dstAttr) {
  var tx = this._createModifier('noop', srcAttr);
  if (dstAttr === undefined || dstAttr === '') {
    tx.done();
  } else {
    tx.done(dstAttr);
  }
};

TransformComposer.prototype.includeAttribute = function (srcAttr, dstAttr) {
  var metaAttr = _TMetadataSpec[srcAttr];
  if (metaAttr !== undefined) {
    this.includeMetadataAttribute(srcAttr, dstAttr);
  } else {
    let op = {
      op: 'include',
      src: srcAttr,
      dst: !dstAttr ? srcAttr : dstAttr,
    };
    op.arrayPaths = new ArrayPaths(op.src, op.dst);
    this._tx_stack.push(op);
  }
};

TransformComposer.prototype.hoistAttributes = function (attr, prefix) {
  this._tx_stack.push({
    op: 'hoist',
    src: attr,
    prefix: prefix,
  });
};

TransformComposer.prototype.removeAttribute = function (attr) {
  this._tx_stack.push({
    op: 'remove',
    src: attr,
  });
};

TransformComposer.prototype.setAttributeDefault = function (dstAttr, value) {
  this._tx_stack.push({
    op: 'default',
    dst: dstAttr,
    val: value,
  });
};

TransformComposer.prototype.removeAttributeDefault = function (dstAttr) {
  // Note, 'val' attribute of operation is undefined
  this._tx_stack.push({
    op: 'default',
    dst: dstAttr,
  });
};

TransformComposer.prototype.setCustomTx = function (customTx) {
  this._tx_custom = customTx;
};

TransformComposer.prototype.getCustomTx = function () {
  return this._tx_custom;
};

/* Mapping Transforms */

function TxMap(transformParent) {
  this._TC = transformParent;
}

TxMap.prototype.lookup = function (mapId, srcAttr, subKey) {
  return this._TC._createModifier('lookup', mapId, srcAttr, subKey);
};

TxMap.prototype.lookupList = function (mapId, srcAttrOrStr) {
  return this._TC._createModifier('lookupList', mapId, srcAttrOrStr);
};

TxMap.prototype.hash = function (stringHash, hashType, srcAttr) {
  switch (hashType) {
    case 'sha256':
    case 'SHA256':
      hashType = 'sha256';
      break;
    default:
      hashType = 'md5';
      break;
  }
  var func = !!stringHash ? 'stringHash' : 'integerHash';
  return this._TC._createModifier(func, hashType, srcAttr);
};

TxMap.prototype.noop = function (srcAttr) {
  return this._TC._createModifier('noop', srcAttr);
};

TxMap.prototype.customAttributeTransform = function (attributeTransformId, funcParamsArray = []) {
  return this._TC._createModifier(
    'custom',
    attributeTransformId,
    Array.prototype.slice.call(funcParamsArray),
  );
};

TxMap.prototype.getNexsetData = function (dataSetId, apiKey, noCache, selectors) {
  return this._TC._createModifier('getNexsetData', dataSetId, apiKey, noCache, selectors);
};

/* Math Transforms */

function TxMath(transformParent) {
  this._TC = transformParent;
}

TxMath.prototype.abs = function (srcAttr) {
  return this._TC._createModifier('abs', srcAttr);
};

TxMath.prototype.avg = function (srcAttr) {
  return this._TC._createModifier('avg', srcAttr);
};

TxMath.prototype.bucket = function (bucketSize, srcAttr) {
  return this._TC._createModifier('bucket', bucketSize, srcAttr);
};

TxMath.prototype.add = function (srcAttr1, srcAttr2) {
  return this._TC._createModifier('add', srcAttr1, srcAttr2);
};

TxMath.prototype.diff = function (srcAttr1, srcAttr2) {
  return this._TC._createModifier('diff', srcAttr1, srcAttr2);
};

TxMath.prototype.multiply = function (srcAttr1, srcAttr2) {
  return this._TC._createModifier('multiply', srcAttr1, srcAttr2);
};

TxMath.prototype.div = function (srcAttr1, srcAttr2) {
  return this._TC._createModifier('div', srcAttr1, srcAttr2);
};

TxMath.prototype.max = function (srcAttr) {
  return this._TC._createModifier('max', srcAttr);
};

TxMath.prototype.min = function (srcAttr) {
  return this._TC._createModifier('min', srcAttr);
};

TxMath.prototype.precision = function (scale, srcAttr) {
  return this._TC._createModifier('precision', scale, srcAttr);
};

TxMath.prototype.toDouble = function (srcAttr) {
  return this._TC._createModifier('toDouble', srcAttr);
};

TxMath.prototype.toInteger = function (srcAttr) {
  return this._TC._createModifier('toInteger', srcAttr);
};

TxMath.prototype.toLong = function (srcAttr) {
  return this._TC._createModifier('toLong', srcAttr);
};

/* Conditional Transforms */
function TxConditional(transformParent) {
  this._TC = transformParent;
}

TxConditional.prototype.equal = function (srcAttr, compareWithAttr) {
  return this._TC._createModifier('equal?', srcAttr, compareWithAttr);
};

TxConditional.prototype.lessThan = function (srcAttr, compareWithAttr) {
  return this._TC._createModifier('lessThan?', srcAttr, compareWithAttr);
};

TxConditional.prototype.greaterThan = function (srcAttr, compareWithAttr) {
  return this._TC._createModifier('greaterThan?', srcAttr, compareWithAttr);
};

TxConditional.prototype.not = function (srcAttr) {
  return this._TC._createModifier('not?', srcAttr);
};

TxConditional.prototype.empty = function (srcAttr) {
  return this._TC._createModifier('empty?', srcAttr);
};

TxConditional.prototype.and = function (srcAttr1, srcAttr2) {
  return this._TC._createModifier('and?', srcAttr1, srcAttr2);
};

TxConditional.prototype.or = function (srcAttr1, srcAttr2) {
  return this._TC._createModifier('or?', srcAttr1, srcAttr2);
};

TxConditional.prototype.ifElse = function (criteria, truthyAttr, falsyAttr) {
  return this._TC._createModifier('ifElse', criteria, truthyAttr, falsyAttr);
};

/* String Transforms */

function TxString(transformParent) {
  this._TC = transformParent;
}

TxString.prototype.concat = function (srcAttr) {
  return arguments.length === 1
    ? this._TC._createModifier('concatSingle', srcAttr)
    : this._TC._createModifier('concatMultiple', Array.prototype.slice.call(arguments));
};

TxString.prototype.join = function (delimiter, srcAttr) {
  if (arguments.length > 2) {
    var joinArgs = ['joinMultiple', delimiter].concat(Array.prototype.slice.call(arguments, 1));
    return this._TC._createModifier.apply(this._TC, joinArgs);
  }
  return this._TC._createModifier('join', delimiter, srcAttr);
};

TxString.prototype.split = function (delimiter, srcAttr) {
  return this._TC._createModifier('split', delimiter, srcAttr);
};

TxString.prototype.toUpper = function (srcAttr) {
  return this._TC._createModifier('toUpper', srcAttr);
};

TxString.prototype.toLower = function (srcAttr) {
  return this._TC._createModifier('toLower', srcAttr);
};

TxString.prototype.toString = function (srcAttr) {
  return this._TC._createModifier('toString', srcAttr);
};

TxString.prototype.replaceAll = function (fromStr, toStr, srcAttr) {
  return this._TC._createModifier('replaceAll', fromStr, toStr, srcAttr);
};

TxString.prototype.substring = function (srcAttr, fromPos, toPos) {
  toPos = arguments.length > 2 ? toPos : null;
  return this._TC._createModifier('substring', fromPos, toPos, srcAttr);
};

TxString.prototype.length = function (srcAttr) {
  return this._TC._createModifier('strlen', srcAttr);
};

TxString.prototype.trim = function (srcAttr) {
  return this._TC._createModifier('trim', srcAttr);
};

TxString.prototype.toJsonString = function (srcAttr) {
  return this._TC._createModifier('toJsonString', srcAttr);
};

TxString.prototype.grok = function (grokPatternStr, srcAttr) {
  return this._TC._createModifier('grok', grokPatternStr, srcAttr);
};

TxString.prototype.regexFind = function (patternStr, srcAttr) {
  return this._TC._createModifier('regexFind', patternStr, srcAttr);
};

TxString.prototype.regexMatch = function (patternStr, srcAttr) {
  return this._TC._createModifier('regexMatch', patternStr, srcAttr);
};

/* Array Transforms */

function TxArray(transformParent) {
  this._TC = transformParent;
}

TxArray.prototype.sort = function (srcAttr) {
  return this._TC._createModifier('array.sort', srcAttr);
};

TxArray.prototype.size = function (srcAttr) {
  return this._TC._createModifier('size', srcAttr);
};

TxArray.prototype.firstElement = function (srcAttr) {
  return this._TC._createModifier('firstElement', srcAttr);
};

TxArray.prototype.lastElement = function (srcAttr) {
  return this._TC._createModifier('lastElement', srcAttr);
};

TxArray.prototype.elementAt = function (index, srcAttr) {
  return this._TC._createModifier('elementAt', index, srcAttr);
};

TxArray.prototype.filter = function (expr, srcAttr) {
  return this._TC._createModifier('filter', expr, srcAttr);
};

/* Extraction Transforms */

function TxExtract(transformParent) {
  this._TC = transformParent;
}

TxExtract.prototype.userAgent = function (uaPart, srcAttr) {
  /*
    Supported uaPart string params:
    device_make
    device_type
    browser
    operating_system
  */
  return this._TC._createModifier('userAgent', uaPart, srcAttr);
};

TxExtract.prototype.geo = function (geoPart, srcAttr) {
  /*
    Supported geoPart string params:
    country
    country_code
    city
    postal
    region
    continent
    latitude
    longitude
    dma
    timezone
    region_code
  */
  return this._TC._createModifier('geo', geoPart, srcAttr);
};

TxExtract.prototype.vin = function (vinPart, srcAttr) {
  /*
    Supported vinPart string params:
    country
    manufacturer
    year
  */
  return this._TC._createModifier('vin', vinPart, srcAttr);
};

/* Date-time Transforms */

function TxDate(transformParent) {
  this._TC = transformParent;
}

TxDate.prototype.iso8601FromEpoch = function (srcAttr) {
  return this._TC._createModifier('iso8601FromEpoch', srcAttr);
};

TxDate.prototype.iso8601FromString = function (srcAttr) {
  return this._TC._createModifier('iso8601FromString', srcAttr);
};

TxDate.prototype.extractDate = function (dateComponentStr, srcAttr) {
  return this._TC._createModifier('extractDate', dateComponentStr, srcAttr);
};

TxDate.prototype.convertTimeZone = function (fromTz = null, toTz, srcAttr) {
  return this._TC._createModifier('convertTimeZone', fromTz, toTz, srcAttr);
};

TxDate.prototype.timestampDifference = function (diffUnit = null, fromTs = null, srcAttr) {
  return this._TC._createModifier('timestampDifference', diffUnit, fromTs, srcAttr);
};
/* Currency Transforms */

function TxCurrency(transformParent) {
  this._TC = transformParent;
}

TxCurrency.prototype.convertString = function (mapId, sym, srcAttr) {
  /* This version emits the conversion in String format */
  return this._TC._createModifier('currency.convertString', mapId, sym, srcAttr);
};

TxCurrency.prototype.convert = function (mapId, sym, srcAttr) {
  /* This version emits the conversion in Number format */
  return this._TC._createModifier('currency.convert', mapId, sym, srcAttr);
};

/* AI Transforms */

function TxAi(transformParent) {
  this._TC = transformParent;
}

TxAi.prototype.oneHotEncoding = function (mapId, srcAttr) {
  return this._TC._createModifier('oneHotEncoding', mapId, srcAttr);
};

/* Filtering */
TransformComposer.prototype.startFilterGroup = function () {
  this._ft_stack.push({ rules: [] });
};

TransformComposer.prototype.endFilterGroup = function () {
  let _ftStackCount = this._ft_stack.length;
  this._ft_stack[_ftStackCount - 1].logicalOp = '&&';
};

TransformComposer.prototype.addFilter = function (attr, op, value, logicalOp = '&&') {
  let _ftStackCount = this._ft_stack.length;
  this._ft_stack[_ftStackCount - 1].rules.push({
    op: 'filter',
    attr: attr,
    fop: op,
    value: value,
    logicalOp: logicalOp,
  });
};

TransformComposer.prototype.beginsWithFilter = function (
  attr,
  value,
  caseSensitive,
  logicalOp = null,
) {
  var i = caseSensitive ? '' : '(?i)';
  var op = '=~';
  var pattern = `/${i}${_.escapeRegExp(value)}.*/`;
  this.addFilter(attr, op, pattern, logicalOp);
};

TransformComposer.prototype.containsFilter = function (
  attr,
  value,
  caseSensitive,
  logicalOp = null,
) {
  var i = caseSensitive ? '' : '(?i)';
  var op = '=~';
  var pattern = `/${i}.*${_.escapeRegExp(value)}.*/`;
  this.addFilter(attr, op, pattern, logicalOp);
};

TransformComposer.prototype.endsWithFilter = function (
  attr,
  value,
  caseSensitive,
  logicalOp = null,
) {
  var i = caseSensitive ? '' : '(?i)';
  var op = '=~';
  var pattern = `/${i}.*${_.escapeRegExp(value)}/`;
  this.addFilter(attr, op, pattern, logicalOp);
};

TransformComposer.prototype.setSortOutput = function (sorted) {
  this._tx_stack.push({ op: 'sort', sorted: sorted });
};

TransformComposer.prototype.endBlock = function () {
  this._tx_stack.push({ op: 'endBlock' });
};

TransformComposer.prototype.decodeFilterSpec = function (encodedFilterSpec) {
  var isEncodedSpec =
    _.isObjectLike(encodedFilterSpec) && _.has(encodedFilterSpec, 'encoding') ? true : false;

  if (!isEncodedSpec) {
    //spec is not encoded, return as is.
    return encodedFilterSpec;
  }

  var decodedFilterSpec =
    encodedFilterSpec.encoding === 'base64' && _.has(encodedFilterSpec, 'filter')
      ? Base64.decode(encodedFilterSpec.filter)
      : encodedFilterSpec;

  return decodedFilterSpec;
};

/* Public Undo/Redo Methods */

TransformComposer.prototype.undo = function (options) {
  this._undo(this._tx_stack, this._tx_redo_stack, options);
};

TransformComposer.prototype.undoFilter = function () {
  this._undo(this._ft_stack, this._ft_redo_stack);
};

TransformComposer.prototype.canUndo = function () {
  return this._canUndo(this._tx_stack);
};

TransformComposer.prototype.canUndoFilter = function () {
  return this._canUndo(this._ft_stack);
};

TransformComposer.prototype.redo = function () {
  this._redo(this._tx_stack, this._tx_redo_stack);
};

TransformComposer.prototype.redoFilter = function () {
  this._redo(this._ft_stack, this._ft_redo_stack);
};

TransformComposer.prototype.canRedo = function () {
  return this._canRedo(this._tx_redo_stack);
};

TransformComposer.prototype.canRedoFilter = function () {
  return this._canRedo(this._ft_redo_stack);
};

/* Private Undo/Redo Methods */

TransformComposer.prototype._undo = function (stack, redoStack, options) {
  var opEntries = [];
  if (_.isObject(options) && !!options.dstAttr) {
    opEntries = _.remove(stack, function (op) {
      return op.modifier.getDstAttr() === options.dstAttr;
    });
  } else {
    opEntries.push(stack.pop());
  }
  _.forEach(opEntries, function (op) {
    redoStack.push(op);
  });
};

TransformComposer.prototype._canUndo = function (stack) {
  return stack.length > 0;
};

TransformComposer.prototype._redo = function (stack, redoStack) {
  var opEntry = redoStack.pop();
  if (opEntry !== undefined) {
    stack.push(opEntry);
  }
};

TransformComposer.prototype._canRedo = function (redoStack) {
  return redoStack.length > 0;
};

function TransformBuilder(tx_features, tx_stack, ft_stack, tx_custom) {
  this._tx_features = tx_features;
  this._tx_stack = tx_stack;
  this._ft_stack = ft_stack;
  this._tx_custom = tx_custom;
  this._dataMaps = [];
  this._dataSets = [];
  this._filters = [];
  this._includeOps = [];
  this._includeAttrs = [];
  this._modifierOps = [];
  this._modifierSpec = {};
  this._modifierSrcs = [];
  this._modifierDsts = [];
  this._modifier2Spec = {};
  this._removerSpec = {};
  this._defaultAttrs = [];
  this._postShiftSpec = {};
  this._hoistShiftSpec = {};
  this._arrayPaths = [];
  this._sortOutput = false;
  this._includeAll = true;
  this._flattenFirst = false;
  this._nestedFirst = false;
  this._hasEndBlock = false;
}

TransformBuilder.prototype.build = function () {
  this._applyStack();
  return this._build();
};

TransformBuilder.prototype._applyStack = function () {
  var self = this;

  _.forEach(this._tx_stack, function (op) {
    switch (op.op) {
      case 'modify':
        self._applyModifier(op);
        break;

      case 'include':
        self._includeOps.push(op);
        break;

      case 'hoist':
        self._applyHoist(op);
        break;

      case 'remove':
        if (self._flattenFirst) {
          self._removerSpec[op.src] = '';
        } else {
          _.set(self._removerSpec, op.src, '');
        }
        break;

      case 'default':
        if (op.val === undefined) {
          _.remove(self._defaultAttrs, function (o) {
            return o.dst === op.dst;
          });
        } else {
          self._defaultAttrs.push(op);
        }
        break;

      case 'setFlattenFirst':
        var flattenFirst = !!op.flattenFirst;
        if (self._flattenFirst !== flattenFirst) {
          self._flattenFirst = flattenFirst;
        }
        break;

      case 'setNestedFirst':
        var nestedFirst = !!op.nestedFirst;
        if (self._nestedFirst !== nestedFirst) {
          self._nestedFirst = nestedFirst;
        }
        break;

      case 'setIncludeAll':
        var includeAll = !!op.includeAll;
        if (self._includeAll !== includeAll) {
          self._includeAll = includeAll;
        }
        break;

      case 'sort':
        self._sortOutput = !!op.sorted;
        break;

      case 'endBlock':
        self._hasEndBlock = true;
        break;

      default:
        break;
    }
  });

  this._applyFilters();
};

TransformBuilder.prototype._applyHoist = function (op) {
  if (_.isEmpty(this._hoistShiftSpec)) {
    // We want this shifter to carry over everything
    // that's already been shifted/modified, and then
    // shift up object attributes to the top level.
    this._hoistShiftSpec['*'] = '&';
  }
  var parts = op.src.split('.');
  _.set(this._hoistShiftSpec, parts[0] + '.@', '&');
  var dst = op.prefix !== undefined ? op.prefix + '&' : '&';
  _.set(this._hoistShiftSpec, op.src, { '*': dst });
};

TransformBuilder.prototype._setModifierWithDepth = function (modifier, modifierSpecs) {
  var dstAttr = modifier.getDstAttr();
  var arrayPaths = modifier._arrayPaths;

  if (this._flattenFirst) {
    modifierSpecs[dstAttr] = modifier.getSpec().replace(/<<D>>/g, 1);
  } else {
    var depth =
      !!arrayPaths && arrayPaths.isValid()
        ? arrayPaths.getDstPostfixDepth()
        : dstAttr.split('.').length;
    _.set(modifierSpecs, dstAttr, modifier.getSpec().replace(/<<D>>/g, depth));
  }
};

TransformBuilder.prototype._applyModifier = function (op) {
  this._modifierOps.push(op);
  this._modifierDsts.push(op.modifier.getDstAttr());
  this._modifierSrcs = _.union(this._modifierSrcs, op.modifier.getSrcAttrs());

  if (!!op.modifier._arrayPaths && op.modifier._arrayPaths.isValid()) {
    this._arrayPaths.push(op.modifier._arrayPaths);
    let postShiftSpec = op.modifier._arrayPaths.getPostShiftSpec();
    if (postShiftSpec !== null) {
      this._postShiftSpec = _.merge(this._postShiftSpec, postShiftSpec);
    }
  }

  if (op.modifier._secondPass) {
    this._setModifierWithDepth(op.modifier, this._modifier2Spec);
  } else {
    this._setModifierWithDepth(op.modifier, this._modifierSpec);
  }

  var maps = op.modifier.getDataMaps();
  if (!!maps) {
    this._dataMaps = _.union(this._dataMaps, maps);
  }
  var sets = op.modifier.getDataSets();
  if (!!sets) {
    this._dataSets = _.union(this._dataSets, sets);
  }
};

TransformBuilder.prototype._applyFilters = function () {
  var self = this;

  _.forOwn(this._ft_stack, (group) => {
    let thisGroup = [];
    _.forOwn(group.rules, (op) => {
      switch (op.op) {
        case 'filter':
          thisGroup.push({
            attr: op.attr,
            op: op.fop,
            logicalOp: op.logicalOp,
            value: op.value,
          });
          break;

        default:
          break;
      }
    });

    self._filters.push({ rules: thisGroup, op: group.logicalOp });
  });
};

TransformBuilder.prototype._getArrayPathsFromSrc = function (attr) {
  return _.find(this._arrayPaths, function (ap) {
    return ap.isValid() ? ap._src.attr === attr : false;
  });
};

TransformBuilder.prototype._prepareFilterAttribute = function (attr) {
  // Filter syntax for dot-notation attributes looks like this:
  // ['a'].['b'].['c']
  // where the dot-notation path is "a.b.c". The bracket-quote
  // enclosure allows for spaces in the string.
  if (typeof attr !== 'string') {
    return null;
  }
  var attrParts = attr.split('.');
  var preparedAttr = '';
  for (var i = 0; i < attrParts.length; i++) {
    preparedAttr += `['${attrParts[i]}']`;
    if (i < attrParts.length - 1) {
      preparedAttr += '.';
    }
  }
  return preparedAttr;
};

/**
 * _buildShift - Builds the "shift" operation for JOLT transformation
 *
 * The shift operation moves source attributes into the output structure.
 * This is part of the "scaffolding" that wraps transformation logic.
 *
 * Shifter Modes:
 * - 'none': No shift needed (include-all mode or only defaults/filters)
 * - 'pre': Shift comes BEFORE modifiers (normal case)
 * - 'post': Shift comes AFTER modifiers (metadata-only edge case)
 * - 'empty': No attributes at all, output is empty (nexla.empty)
 *
 * Key modification for multi-block support:
 * When there are NO included/silent attributes but there ARE filters or defaults,
 * we set mode to 'none' instead of 'empty'. This allows:
 * - FILTERS-only blocks to output nexla.filter (not nexla.empty)
 * - DEFAULTS-only blocks to output default operation (not nexla.empty)
 *
 * @param {Object} shifter - Object with mode and transformation spec
 *        shifter.mode: 'none' | 'pre' | 'post' | 'empty'
 *        shifter.t: { operation: 'shift', spec: {...} }
 */
TransformBuilder.prototype._buildShift = function (shifter) {
  if (this._includeAll) {
    shifter.mode = 'none';
    return;
  }

  var includedAttrs = [];
  var silentAttrs = null;
  var self = this;

  _.forEach(this._includeOps, function (op) {
    let dst = op.arrayPaths.isValid() ? op.arrayPaths.getShiftDst() : op.dst;
    if (self._flattenFirst) {
      shifter.t.spec[op.src] = op.dst;
    } else {
      _.set(shifter.t.spec, op.src, dst);
    }
    includedAttrs.push(op.dst);
  });

  silentAttrs = _.difference(this._modifierSrcs, includedAttrs);
  var metaAttrs = _.filter(silentAttrs, (a) => {
    var metaAttr = _TMetadataSpec[a];
    return metaAttr !== undefined;
  });

  var tmp = [];
  _.forEach(silentAttrs, function (sAttr) {
    // Here we look to see if any of the silent attributes
    // are included in explicitly-included trees. See NEX-3456.
    var i;
    for (i = 0; i < includedAttrs.length; i++) {
      if (sAttr.startsWith(includedAttrs[i])) {
        tmp.push(sAttr);
        break;
      }
    }
  });
  silentAttrs = _.difference(silentAttrs, tmp);

  var haveSilentAttrs = silentAttrs.length > 0;
  var haveIncludedAttrs = includedAttrs.length > 0;

  if (!haveIncludedAttrs && haveSilentAttrs && metaAttrs.length === silentAttrs.length) {
    // This is a corner case. No regular attributes were included and all
    // modifiers refer only to metadata attributes, which cannot be shifted
    // into the output until they are copied to regular attributes. So here
    // we generate a shift operation to be included after the modifier block.
    // It includes everything from the default and modifier blocks.
    shifter.mode = 'post';
    _.forOwn(this._defaultAttrs, (v, k) => {
      var dst = v.dst.split('.')[0];
      shifter.t.spec[dst] = dst;
    });
    _.forEach(this._modifierDsts, (d) => {
      shifter.t.spec[d] = d;
    });
    return;
  }

  if (!haveIncludedAttrs && !haveSilentAttrs) {
    // ─────────────────────────────────────────────────────────────────────────
    // CRITICAL: Empty attributes handling for multi-block support
    //
    // When there are no included/silent attributes:
    //
    // SCENARIO 1: We have defaults OR filters
    //   - Set mode to 'none' (skip shift operation)
    //   - Continue to _buildDefaults() and _buildFilters()
    //   - This allows FILTERS-only and DEFAULTS-only blocks to work
    //
    //   Example: CODE → TRANSFORMS → FILTERS
    //   The FILTERS block has no modify operations, but it DOES have filters
    //   Without this check, it would output nexla.empty instead of nexla.filter
    //
    // SCENARIO 2: No defaults AND no filters
    //   - Set mode to 'empty'
    //   - Output nexla.empty (entire transformation produces empty record)
    //   - The _build() method will return early
    //
    // The filters check (!_.isEmpty(this._filters)) was added for multi-block
    // support to enable standalone FILTERS blocks that come after CODE blocks
    // ─────────────────────────────────────────────────────────────────────────
    if (this._defaultAttrs.length > 0 || !_.isEmpty(this._filters)) {
      shifter.mode = 'none';
    } else {
      shifter.mode = 'empty';
      shifter.t = { operation: 'nexla.empty' };
    }
    return;
  }

  shifter.mode = 'pre';

  _.forEach(silentAttrs, function (attr) {
    // Here we include the silent attributes in the shift to make
    // them available to subsequent modifier ops. Then we'll remove
    // the truly silent ones in _buildSilentRemover().
    // Skip metadata attributes. They are handled seperately via
    // the Jolt "context" and can't be shifted around like regular attributes.
    var metaAttr = _TMetadataSpec[attr];
    if (metaAttr !== undefined) {
      return;
    }

    if (self._flattenFirst) {
      shifter.t.spec[attr] = attr;
    } else {
      var arrayPaths = self._getArrayPathsFromSrc(attr);
      var value = !!arrayPaths && arrayPaths.isValid() ? arrayPaths.getSrcShift() : attr;
      _.set(shifter.t.spec, attr, value);
    }
  });

  this._buildSilentRemover(shifter, silentAttrs);
};

TransformBuilder.prototype._buildSilentRemover = function (shifter, silentAttrs) {
  var self = this;
  var trulySilent = [];

  // "silent" attributes are included as source attributes in modifier calls,
  // but not included in an explicit includeAttribute() call. They are shifted
  // automatically when Include-All is false. "trulySilent" attributes are silent
  // attributes that are also not represented in any modifier destination attribute
  // path. They must be automatically included in the remover at the end of the
  // transform sequence.

  _.forEach(this._modifierOps, function (op) {
    if (op.modifier.hasArrayPaths()) {
      // Here we add fully-specified, JsonPath-style modifier destination
      // paths into _modifierDsts so they will match in the _.difference()
      // call below that finds the truly-silent attributes (the ones that
      // should be removed).
      let arrayPaths = op.modifier._arrayPaths;
      let dst = arrayPaths.isOverlapping() ? arrayPaths._src.attr : arrayPaths._dst.attr;
      self._modifierDsts = _.union(self._modifierDsts, [dst]);
    }
  });

  var diff = _.difference(silentAttrs, self._modifierDsts);
  _.forEach(diff, function (attr) {
    if (self._modifierDsts.indexOf(attr.split('.')[0]) < 0) {
      trulySilent.push(attr);
    }
  });

  var mutableShift = _.cloneDeep(shifter.t.spec);
  var removePaths = [];

  _.forEach(this._modifierOps, function (op) {
    if (op.modifier.hasArrayPaths()) {
      let arrayPaths = op.modifier._arrayPaths;
      if (arrayPaths.requiresPostShift()) {
        // Here we add destination paths to the mutable copy
        // of the shifter that result from object array transformations
        // into destination arrays different from the source. These
        // are not captured by the silent shifter in _buildShift()
        // because there is nothing on the left to shift for them.
        // They only exist after the modifier runs. They MUST not
        // not be siliently removed.
        _.set(mutableShift, op.modifier.getDstAttr(), 'ignored');
      }
    }
  });

  _.forEach(trulySilent, (attr) => {
    var metaAttr = _TMetadataSpec[attr];
    if (metaAttr !== undefined) {
      return;
    }
    // Prune the silent attribute paths out of the copy of the
    // shift spec, and add each returned path to the list of
    // paths to include in the remove spec.
    var arrayPaths = self._getArrayPathsFromSrc(attr);
    if (!!arrayPaths && arrayPaths.isValid()) {
      attr = arrayPaths.getSrcTraverse();
    }
    removePaths.push(self._deleteNested(0, attr.split('.'), mutableShift));
  });

  _.forEach(removePaths, (path) => {
    var metaAttr = _TMetadataSpec[path];
    if (metaAttr !== undefined) {
      return;
    }

    if (self._flattenFirst) {
      self._removerSpec[path] = '';
    } else {
      _.set(self._removerSpec, path, '');
    }
  });
};

TransformBuilder.prototype._deleteNested = function (curDepth, attrParts, spec) {
  // _deleteNested deletes the nested attribute described by
  // the 'attrParts' array from the 'spec' object. It recurses
  // back up the attribute path, either deleting the resulting empty
  // sub-path or returning the first non-empty sub-path.

  if (curDepth === attrParts.length - 1) {
    if (!_.isNil(spec)) {
      delete spec[attrParts[attrParts.length - 1]];
    }
    return attrParts.join('.');
  }

  var emptyPath = this._deleteNested(curDepth + 1, attrParts, spec[attrParts[curDepth]]);

  if (_.isEmpty(spec[attrParts[curDepth]])) {
    delete spec[attrParts[curDepth]];
    return _.slice(attrParts, 0, curDepth + 1).join('.');
  }

  return emptyPath;
};

TransformBuilder.prototype._build = function () {
  var tx = {
    version: 1,
    data_maps: this._dataMaps,
    transforms: [],
  };

  if (this._tx_features.include_data_set_deps) {
    tx['data_sets'] = this._dataSets;
  }

  if (this._tx_custom) {
    tx.transforms = this._tx_custom;
    return tx;
  }

  var shifter = { mode: 'none', t: { operation: 'shift', spec: {} } };
  this._buildShift(shifter);

  if (shifter.mode === 'empty' || shifter.mode === 'pre') {
    tx.transforms.push(shifter.t);
  }

  if (shifter.mode === 'empty') {
    return tx;
  }

  this._buildDefaults(tx);
  this._buildModifiers(tx, shifter);

  if (shifter.mode === 'post') {
    tx.transforms.push(shifter.t);
  }

  if (this._flattenFirst) {
    tx.transforms.push({
      operation: 'nexla.flatten',
    });
  }

  if (this._nestedFirst) {
    tx.transforms.push({
      operation: 'nexla.nest',
    });
  }

  if (!_.isEmpty(this._removerSpec)) {
    tx.transforms.push({
      operation: 'remove',
      spec: this._removerSpec,
    });
  }

  if (!_.isEmpty(this._postShiftSpec)) {
    // Note, post-shifter MUST come after the remover.
    tx.transforms.push({ operation: 'shift', spec: this._postShiftSpec });
  }

  if (!_.isEmpty(this._hoistShiftSpec)) {
    // Note, hoist-shifter MUST come after the post-shifter.
    tx.transforms.push({ operation: 'shift', spec: this._hoistShiftSpec });
  }

  if (this._sortOutput) {
    tx.transforms.push({ operation: 'sort' });
  }

  this._buildFilters(tx);

  if (this._hasEndBlock) {
    tx.transforms.push({ operation: 'nexla.end_block' });
  }

  return tx;
};

TransformBuilder.prototype._buildDefaults = function (tx) {
  if (_.isEmpty(this._defaultAttrs)) {
    return;
  }

  var self = this;
  var t = { operation: 'default', spec: {} };

  _.forEach(this._defaultAttrs, function (op) {
    if (self._flattenFirst) {
      t.spec[op.dst] = op.val;
    } else {
      _.set(t.spec, op.dst, op.val);
    }
  });
  tx.transforms.push(t);
};

TransformBuilder.prototype._buildModifiers = function (tx, shifter) {
  if (!_.isEmpty(this._modifierSpec)) {
    tx.transforms.push({
      operation: 'nexla.modify',
      spec: this._modifierSpec,
    });
  }

  if (!_.isEmpty(this._modifier2Spec)) {
    tx.transforms.push({
      operation: 'nexla.modify',
      spec: this._modifier2Spec,
    });
  }
};

TransformBuilder.prototype._buildFilters = function (tx) {
  if (_.isEmpty(this._filters)) {
    return;
  }

  const escapeQuotes = (str) => str?.replaceAll("'", "\\'");
  var self = this;
  var conditions = '';

  _.forEach(this._filters, (group, idx, filterGroups) => {
    // {rules:[...],op[...]}
    if (idx > 0) {
      conditions += ` ${filterGroups[idx - 1].op} `;
    }
    conditions += `(`;
    _.forEach(group.rules, function (f, index, filters) {
      const valueLiteral =
        typeof f.value === 'string' ? `'${escapeQuotes(f.value)}'` : `${f.value}`;
      if (index > 0 && filters[index - 1].logicalOp !== null) {
        conditions += ` ${filters[index - 1].logicalOp} `;
      }
      switch (f.op) {
        case 'in':
        case 'nin':
          conditions += `${valueLiteral} ${f.op} $.${self._prepareFilterAttribute(f.attr)}`;
          break;
        case '=~':
          conditions += `$.${self._prepareFilterAttribute(f.attr)} ${f.op} ${f.value}`;
          break;

        case '@':
        case '!@':
          conditions += `${f.op}.${self._prepareFilterAttribute(f.attr)}`;
          break;

        default:
          conditions += `$.${self._prepareFilterAttribute(f.attr)} ${f.op} `;
          conditions += valueLiteral;
          break;
      }
    });

    conditions += `)`;
  });

  var filterStr = `$.[?(${conditions})]`;
  tx.transforms.push({
    operation: 'nexla.filter',
    spec: this._tx_features.base64_filters
      ? {
          encoding: 'base64',
          filter: Base64.encode(filterStr),
        }
      : filterStr,
  });
};

function StateBuilder(tx_stack, ft_stack, tx_custom, tx_features) {
  this._tx_stack = tx_stack;
  this._ft_stack = ft_stack;
  this._tx_custom = tx_custom;
  this._tx_features = tx_features;
  this._tx_stateStack = [];
}

StateBuilder.prototype.build = function () {
  this._applyTxStack();
  return this._build();
};

StateBuilder.prototype._applyTxStack = function () {
  var self = this;

  _.forEach(this._tx_stack, function (op) {
    let stateOp = { op: op.op };
    switch (op.op) {
      case 'modify':
        stateOp.modifier = op.modifier.getStateObject();
        self._tx_stateStack.push(stateOp);
        break;

      default:
        self._tx_stateStack.push(op);
        break;
    }
  });
};

StateBuilder.prototype._build = function () {
  return {
    _tx_stack: this._tx_stateStack,
    _ft_stack: this._ft_stack,
    _tx_custom: this._tx_custom,
    _tx_features: this._tx_features,
  };
};
